import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  InputAdornment,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import WarningIcon from "@mui/icons-material/Warning";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import { useTranslation } from "react-i18next";

/**
 * Admin Inventory Page component for managing product inventory
 * @returns {JSX.Element} The Admin Inventory Page component
 */
const AdminInventoryPage = () => {
  const { t } = useTranslation();
  const [inventory, setInventory] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Mock data for demonstration
  useEffect(() => {
    // This would typically be an API call
    setInventory([
      {
        id: 1,
        sku: "PROD-A-001",
        name: "Product A",
        category: "Electronics",
        quantity: 150,
        reorderLevel: 20,
        location: "Warehouse A, Shelf 3",
        status: "in_stock",
        lastUpdated: "2023-03-10",
        history: [
          {
            date: "2023-03-10",
            type: "restock",
            quantity: 50,
            notes: "Regular restock",
          },
          {
            date: "2023-03-05",
            type: "sale",
            quantity: -10,
            notes: "Order #ORD-2023-001",
          },
        ],
      },
      {
        id: 2,
        sku: "PROD-B-002",
        name: "Product B",
        category: "Office Supplies",
        quantity: 75,
        reorderLevel: 30,
        location: "Warehouse A, Shelf 5",
        status: "in_stock",
        lastUpdated: "2023-03-12",
        history: [
          {
            date: "2023-03-12",
            type: "restock",
            quantity: 25,
            notes: "Regular restock",
          },
          {
            date: "2023-03-08",
            type: "sale",
            quantity: -5,
            notes: "Order #ORD-2023-002",
          },
        ],
      },
      {
        id: 3,
        sku: "PROD-C-003",
        name: "Product C",
        category: "Electronics",
        quantity: 15,
        reorderLevel: 20,
        location: "Warehouse B, Shelf 1",
        status: "low_stock",
        lastUpdated: "2023-03-15",
        history: [
          {
            date: "2023-03-15",
            type: "sale",
            quantity: -10,
            notes: "Order #ORD-2023-003",
          },
          {
            date: "2023-03-01",
            type: "restock",
            quantity: 30,
            notes: "Regular restock",
          },
        ],
      },
      {
        id: 4,
        sku: "PROD-D-004",
        name: "Product D",
        category: "Furniture",
        quantity: 0,
        reorderLevel: 5,
        location: "Warehouse B, Shelf 8",
        status: "out_of_stock",
        lastUpdated: "2023-03-18",
        history: [
          {
            date: "2023-03-18",
            type: "sale",
            quantity: -5,
            notes: "Order #ORD-2023-004",
          },
          {
            date: "2023-02-28",
            type: "restock",
            quantity: 10,
            notes: "Regular restock",
          },
        ],
      },
    ]);
  }, []);

  const handleOpenDialog = (item = null) => {
    setCurrentItem(
      item || {
        sku: "",
        name: "",
        category: "",
        quantity: 0,
        reorderLevel: 0,
        location: "",
        status: "in_stock",
        lastUpdated: new Date().toISOString().split("T")[0],
        history: [],
      }
    );
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenHistoryDialog = (item) => {
    setCurrentItem(item);
    setOpenHistoryDialog(true);
  };

  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false);
  };

  const handleUpdateInventory = () => {
    if (currentItem.id) {
      // Update existing item
      setInventory(
        inventory.map((item) =>
          item.id === currentItem.id ? currentItem : item
        )
      );
      setSnackbar({
        open: true,
        message: t("Inventory item updated successfully"),
        severity: "success",
      });
    } else {
      // Add new item
      setInventory([...inventory, { ...currentItem, id: Date.now() }]);
      setSnackbar({
        open: true,
        message: t("Inventory item added successfully"),
        severity: "success",
      });
    }
    handleCloseDialog();
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentItem({ ...currentItem, [name]: value });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "in_stock":
        return "success";
      case "low_stock":
        return "warning";
      case "out_of_stock":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "in_stock":
        return t("In Stock");
      case "low_stock":
        return t("Low Stock");
      case "out_of_stock":
        return t("Out of Stock");
      default:
        return status;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t("Inventory Management")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t("Add Inventory Item")}
        </Button>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table stickyHeader aria-label="inventory table">
            <TableHead>
              <TableRow>
                <TableCell>{t("SKU")}</TableCell>
                <TableCell>{t("Product Name")}</TableCell>
                <TableCell>{t("Category")}</TableCell>
                <TableCell>{t("Quantity")}</TableCell>
                <TableCell>{t("Reorder Level")}</TableCell>
                <TableCell>{t("Location")}</TableCell>
                <TableCell>{t("Status")}</TableCell>
                <TableCell>{t("Last Updated")}</TableCell>
                <TableCell>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {inventory.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.sku}</TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell>
                    {item.quantity}
                    {item.quantity <= item.reorderLevel && (
                      <Tooltip title={t("Below reorder level")}>
                        <WarningIcon
                          color="warning"
                          fontSize="small"
                          sx={{ ml: 1, verticalAlign: "middle" }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>{item.reorderLevel}</TableCell>
                  <TableCell>{item.location}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(item.status)}
                      color={getStatusColor(item.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{item.lastUpdated}</TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(item)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="secondary"
                      onClick={() => handleOpenHistoryDialog(item)}
                    >
                      <HistoryIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit/Add Inventory Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentItem && currentItem.id
            ? t("Edit Inventory Item")
            : t("Add Inventory Item")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="sku"
                label={t("SKU")}
                fullWidth
                value={currentItem?.sku || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label={t("Product Name")}
                fullWidth
                value={currentItem?.name || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="category"
                label={t("Category")}
                fullWidth
                value={currentItem?.category || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="location"
                label={t("Location")}
                fullWidth
                value={currentItem?.location || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="quantity"
                label={t("Quantity")}
                type="number"
                fullWidth
                value={currentItem?.quantity || 0}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="reorderLevel"
                label={t("Reorder Level")}
                type="number"
                fullWidth
                value={currentItem?.reorderLevel || 0}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Status")}</InputLabel>
                <Select
                  name="status"
                  value={currentItem?.status || "in_stock"}
                  onChange={handleInputChange}
                  label={t("Status")}
                >
                  <MenuItem value="in_stock">{t("In Stock")}</MenuItem>
                  <MenuItem value="low_stock">{t("Low Stock")}</MenuItem>
                  <MenuItem value="out_of_stock">{t("Out of Stock")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="lastUpdated"
                label={t("Last Updated")}
                type="date"
                fullWidth
                value={currentItem?.lastUpdated || ""}
                onChange={handleInputChange}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            {currentItem && currentItem.id && (
              <Grid item xs={12}>
                <TextField
                  name="adjustmentQuantity"
                  label={t("Adjustment Quantity")}
                  type="number"
                  fullWidth
                  helperText={t(
                    "Enter positive value for restock, negative for removal"
                  )}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+/-</InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button
            onClick={handleUpdateInventory}
            variant="contained"
            color="primary"
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inventory History Dialog */}
      <Dialog
        open={openHistoryDialog}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("Inventory History")}</DialogTitle>
        <DialogContent>
          {currentItem && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {currentItem.name} ({currentItem.sku})
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("Date")}</TableCell>
                      <TableCell>{t("Type")}</TableCell>
                      <TableCell align="right">
                        {t("Quantity Change")}
                      </TableCell>
                      <TableCell>{t("Notes")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentItem.history.map((record, index) => (
                      <TableRow key={index}>
                        <TableCell>{record.date}</TableCell>
                        <TableCell>
                          {record.type === "restock" ? t("Restock") : t("Sale")}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              record.quantity > 0
                                ? "success.main"
                                : "error.main",
                          }}
                        >
                          {record.quantity > 0 ? "+" : ""}
                          {record.quantity}
                        </TableCell>
                        <TableCell>{record.notes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistoryDialog}>{t("Close")}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminInventoryPage;
